import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import set from 'lodash/set';
import BorderEditor from 'common/components/BorderPicker';
import ColorPickerEditor from 'common/components/ColorPicker';
import Spacer from 'common/components/Spacer';
import { containerPaddingS } from 'common/styles/variables';
import { useNode } from '@splashthat/splash-cms-engine';

const FIRST_COLOR_LABEL = 'Default';
const SECOND_COLOR_LABEL = 'Hover';
const TRANSPARENT_FALLBACK = { value: 'transparent' };

export default function BorderTools({
    borderLabel,
    colors,
    dataQa,
    ariaLabel,
    borderColorPath,
    borderColorHoverPath,
    borderWidthPath,
    borderRadiusPath,
    hasHover,
}) {
    const node = useNode();
    const colorList = colors.map((color, index) => ({
        id: index,
        value: color,
    }));
    const borderWidths = {
        sizes: get(node, borderWidthPath),
    };

    const updateBorderColor = (id) =>
        node.actions.setProp((editedNode) => {
            set(editedNode, borderColorPath, id);
            return editedNode;
        });

    const updateBorderColorHover = (id) =>
        node.actions.setProp((editedNode) => {
            set(editedNode, borderColorHoverPath, id);
            return editedNode;
        });

    const updateAllBorderWidths = (width) =>
        node.actions.setProp((editedNode) => {
            const updatedBorderWidths = {
                top: width,
                left: width,
                right: width,
                bottom: width,
            };

            set(editedNode, borderWidthPath, updatedBorderWidths);

            return editedNode;
        });

    const updateIndividualBorderWidth = (width, position) =>
        node.actions.setProp((editedNode) => {
            set(editedNode, `${borderWidthPath}.${position}`, width);
            return editedNode;
        });

    const updateBorderRadius = (radius) =>
        node.actions.setProp((editedNode) => {
            set(editedNode, borderRadiusPath, radius);
            return editedNode;
        });

    const hoverProps = hasHover
        ? {
              // We don't include a label on the first color if there is no second color
              firstColorLabel: FIRST_COLOR_LABEL,
              secondColor: colorList[get(node, borderColorHoverPath)] || TRANSPARENT_FALLBACK,
              changeSecondColor: updateBorderColorHover,
              secondColorLabel: SECOND_COLOR_LABEL,
          }
        : {};

    return (
        <React.Fragment>
            <BorderEditor
                borderRadius={get(node, borderRadiusPath)}
                onBorderRadiusChanged={updateBorderRadius}
                borderWidths={borderWidths}
                onBorderWidthsChanged={updateAllBorderWidths}
                onChangeTopBorderWidth={(width) => updateIndividualBorderWidth(width, 'top')}
                onChangeBottomBorderWidth={(width) => updateIndividualBorderWidth(width, 'bottom')}
                onChangeRightBorderWidth={(width) => updateIndividualBorderWidth(width, 'right')}
                onChangeLeftBorderWidth={(width) => updateIndividualBorderWidth(width, 'left')}
                borderOptionsQa={`${dataQa}--borderOptions`}
                borderDropdownQa={`${dataQa}--borderDropdown`}
                componentLabel={borderLabel}
                aria-label={`${ariaLabel} Border Width Picker`}
            />
            <Spacer $spacing={containerPaddingS} />
            <ColorPickerEditor
                colorList={colorList}
                firstColor={colorList[get(node, borderColorPath)] || TRANSPARENT_FALLBACK}
                changeFirstColor={updateBorderColor}
                aria-label={`${ariaLabel} Border Color Picker`}
                qaId={`${dataQa}--borderColor`}
                {...hoverProps}
            />
        </React.Fragment>
    );
}

BorderTools.propTypes = {
    borderLabel: PropTypes.string,
    borderColorPath: PropTypes.string,
    borderColorHoverPath: PropTypes.string,
    borderWidthPath: PropTypes.string,
    borderRadiusPath: PropTypes.string,
    colors: PropTypes.array.isRequired,
    dataQa: PropTypes.string,
    ariaLabel: PropTypes.string,
    hasHover: PropTypes.bool,
};

BorderTools.defaultProps = {
    borderColorPath: 'styles.borderColor',
    borderColorHoverPath: 'styles.borderColorHover',
    borderWidthPath: 'styles.borderWidth',
    borderRadiusPath: 'styles.borderRadius',
    borderLabel: 'Border',
    dataQa: 'defaultBorderTools',
    ariaLabel: 'Default',
    hasHover: true,
};
